import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Activity } from 'lucide-react';
import client from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import GameArtwork from '../../components/GameArtwork';
import { SECTIONS, sectionHook } from '../../lib/domHooks';

// Referans vitrinindeki canlı bahis tablosu: üç sekme ve sağda akışı
// durduran bir anahtar.
//
// Akış **yoklamayla** tazelenir (sohbetle aynı gerekçe: uygulama tek uvicorn
// worker ile çalışır, oyuncu başına kalıcı soket açmak sağlayıcı
// webhook'larıyla aynı süreci yorar). Anahtar kapatıldığında yoklama da
// durur — satırların altından kayması tabloyu okumayı imkânsız kılıyordu.
const POLL_MS = 8000;

const TABS = [
  { key: 'all', label: 'Tüm Bahisler' },
  { key: 'high', label: 'Yüksek Bahisler' },
  { key: 'mine', label: 'Bahislerim', auth: true },
];

const money = (value) => Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const HerkulLiveBets = () => {
  const { user } = useAuth();
  const [scope, setScope] = useState('all');
  const [rows, setRows] = useState([]);
  const [live, setLive] = useState(true);
  const [loaded, setLoaded] = useState(false);
  // Akış durdurulduğunda bekleyen zamanlayıcı da iptal edilmelidir.
  const timer = useRef(null);

  const load = useCallback(async (which) => {
    try {
      const { data } = await client.get('/games/live-bets', {
        params: { scope: which, limit: 12 },
      });
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch {
      // Tablo yan bir vitrin öğesidir; geçici ağ hatası için ana sayfaya
      // hata basmak gürültü olurdu.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    setLoaded(false);
    load(scope);
  }, [scope, load]);

  useEffect(() => {
    if (!live) return undefined;
    const tick = () => {
      timer.current = setTimeout(async () => {
        await load(scope);
        tick();
      }, POLL_MS);
    };
    tick();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [live, scope, load]);

  // Üye çıkış yaparsa "Bahislerim" sekmesinde kalmak anlamsızdır.
  useEffect(() => {
    if (!user && scope === 'mine') setScope('all');
  }, [user, scope]);

  const tabs = TABS.filter((tab) => !tab.auth || user);

  return (
    <section className="herkul-bets" {...sectionHook(SECTIONS.LIVE_BETS)}>
      <div className="herkul-bets-head">
        <div className="herkul-bets-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={scope === tab.key}
              className={`herkul-bets-tab ${scope === tab.key ? 'is-active' : ''}`}
              onClick={() => setScope(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`herkul-bets-live ${live ? 'is-on' : ''}`}
          onClick={() => setLive((on) => !on)}
          aria-pressed={live}
          title={live ? 'Akışı durdur' : 'Akışı sürdür'}
        >
          <Activity size={13} />
          <span>{live ? 'Canlı' : 'Duraklatıldı'}</span>
        </button>
      </div>

      <div className="herkul-bets-wrap">
        <table className="herkul-bets-table">
          <thead>
            <tr>
              <th>Oyun</th>
              <th>Kullanıcı</th>
              <th className="is-num">Bahis Miktarı</th>
              <th className="is-num">Çarpan</th>
              <th className="is-num">Ödeme</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.user}-${row.created_at}-${index}`}>
                <td>
                  <span className="herkul-bets-game">
                    <span className="herkul-bets-art">
                      <GameArtwork src={row.image} alt={row.game_name} />
                    </span>
                    <span className="herkul-bets-name">{row.game_name}</span>
                  </span>
                </td>
                <td className="herkul-bets-user">{row.user}</td>
                <td className="is-num">{money(row.bet)}</td>
                <td className="is-num">
                  {/* Kazandıran tur vurgulanır: tabloda gözü ilk çeken
                      şey turun sonucudur. */}
                  <span className={`herkul-bets-mult ${row.win > row.bet ? 'is-win' : ''}`}>
                    {Number(row.multiplier || 0).toLocaleString('tr-TR', {
                      maximumFractionDigits: 2,
                    })}x
                  </span>
                </td>
                <td className={`is-num ${row.win > 0 ? 'is-payout' : ''}`}>
                  {money(row.win)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {loaded && rows.length === 0 && (
          <p className="herkul-bets-empty">
            {scope === 'mine'
              ? 'Henüz bir bahsin yok. Bir oyun aç, ilk turun burada görünsün.'
              : 'Şu anda gösterilecek tur yok.'}
          </p>
        )}
      </div>
    </section>
  );
};

export default HerkulLiveBets;
